// crewbit-admin/src/components/money/T2125ProgressCard.tsx
//
// T2125 categorization progress. Reads from FinancialSummary.t2125_progress
// (Session 134 envelope extension; admin TS interface extended Session 136
// Commit 1).
//
// Visibility: always rendered when tax_mode !== 'exempt' (T2125 = income tax,
// separate from HST status — parent gates).
//
// Edge case per §9 failure mode #14: when ytd_expenses_count === 0, suppress
// celebratory "100% categorized" copy and show "Log your first expense" CTA.
//
// Click routes to /money/expenses (categorize uncategorized) OR /money/expenses
// when YTD count is zero.
//
// Spec §Tax.7.2 + §Tax.D4.

import { Sparkle, ChevronRight } from 'lucide-react';
import type { FinancialSummary } from '../../hooks/useFinancialSummary';
import { palette, admin, radii, spacing, shadow, transition } from '../../styles/tokens';

interface T2125ProgressCardProps {
  progress: FinancialSummary['t2125_progress'];
  onClick: () => void;
}

export function T2125ProgressCard({ progress, onClick }: T2125ProgressCardProps) {
  const isEmpty = progress.ytd_expenses_count === 0;
  const isComplete = !isEmpty && progress.uncategorized_count === 0;
  const fillPct = Math.min(progress.completion_pct, 100);

  let statusText: string;
  let ctaText: string;
  if (isEmpty) {
    statusText = 'No expenses logged yet — categorization starts when you log your first.';
    ctaText = 'Log your first expense';
  } else if (isComplete) {
    statusText = `All ${progress.categorized_count} expenses categorized for T2125.`;
    ctaText = 'Review categories';
  } else {
    statusText = `${progress.completion_pct}% categorized · ${progress.uncategorized_count} left to review`;
    ctaText = `Categorize ${progress.uncategorized_count}`;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.sm,
        width: '100%',
        padding: spacing.md,
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: admin.cardRadius,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        boxShadow: shadow.xs,
        transition: `box-shadow ${transition.fast}, border-color ${transition.fast}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = shadow.cardHover;
        e.currentTarget.style.borderColor = admin.borderHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = shadow.xs;
        e.currentTarget.style.borderColor = palette.g150;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: admin.header, flex: 1 }}>
          T2125 readiness
        </div>
        {isComplete && <Sparkle size={14} color={palette.green} />}
      </div>

      <div
        style={{
          height: 8,
          background: palette.g100,
          borderRadius: radii.full,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${fillPct}%`,
            height: '100%',
            background: isComplete ? palette.green : palette.amber,
            borderRadius: radii.full,
            transition: `width ${transition.normal}`,
          }}
          aria-hidden="true"
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm }}>
        <div style={{ fontSize: 12, color: palette.g600, fontVariantNumeric: 'tabular-nums', flex: 1 }}>
          {statusText}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: palette.amber, flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{ctaText}</span>
          <ChevronRight size={13} strokeWidth={2.5} />
        </div>
      </div>
    </button>
  );
}
